/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import get from 'lodash/get';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import some from 'lodash/some';
import { calendarDateToUtcString, localToday } from 'sarsaparilla';
import { CalendarDate } from '@internationalized/date';
import { sortCampsitesByName, getSortOrderOfStringWithNumbers } from './string_utils';
import {
    SITE,
    LOOP,
    AVAILABILITY_LABEL_SORT_PRIORITY,
    AVAILABILITY_STATUS_SORT_PRIORITY,
    CAMPSITE_AVAILABLE,
    CAMPSITE_EARLY_ACCESS_AVAILABLE,
    CAMPSITE_RESERVED,
    LABEL_UNAVAILABLE,
} from '../constants/availability';
import { NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX } from '../constants';
import { isValidUUID } from './utils';
import type {
    CampsiteAvailabilities,
    LocalCampsite,
    Campsite,
    LineItem,
} from '../../../types/camping';
import { UTCStringToCalendarDate, formatAvailabilityAccessor } from './dateUtils';
import { FEE_TYPES, FEE_TYPES_ORDER } from '../constants/orderDetails';

export const getSortedDataBySiteAndLoop = ({
    dataSource,
    isAsc,
    column,
}: {
    dataSource: Campsite[] | LocalCampsite[];
    isAsc: boolean;
    column: string;
}) => {
    let sortedDataSource;
    const secondarySortKey = column === SITE ? LOOP : SITE;
    const data = cloneDeep(dataSource);

    if (column === SITE) {
        sortedDataSource = !isAsc
            ? sortCampsitesByName(data, SITE, LOOP).reverse()
            : sortCampsitesByName(data, SITE, LOOP);
    } else if (column === LOOP) {
        sortedDataSource = !isAsc
            ? sortCampsitesByName(data, LOOP, SITE).reverse()
            : sortCampsitesByName(data, LOOP, SITE);
    } else {
        sortedDataSource = !isAsc
            ? sortBy(data, [column, secondarySortKey]).reverse()
            : sortBy(data, [column, secondarySortKey]);
    }

    return sortedDataSource;
};

/**
 * return a weighed count of consecutive available dates at a campsite
 * @param {CalendarDate} startRange - range start
 * @param {CalendarDate} endRange - range end
 * @param {object} availabilities - availability statuses at a campsite by dates
 * @returns {number} - count of consecutive availabilities from the [availabilities] object
 */
const countMaxContinuousAvailability = (
    startRange: CalendarDate,
    endRange: CalendarDate,
    availabilities: CampsiteAvailabilities
) => {
    let currentMax = 0;
    let currentCount = 0;

    for (let day = startRange; day.compare(endRange) < 0; day = day.add({ days: 1 })) {
        const dayKey = formatAvailabilityAccessor(day);
        const status = get(availabilities, dayKey, null);

        if (status) {
            /* if date is AVAILABLE, update 'currentCount' */
            if (
                status === CAMPSITE_AVAILABLE ||
                status === CAMPSITE_EARLY_ACCESS_AVAILABLE
            ) {
                currentCount += 1;
            } else {
                /* else if date is unavailable, continuity is broken.
            Update 'currentMax'; reset 'currentCount' to 0 */
                currentMax = Math.max(currentMax, currentCount);
                currentCount = 0;
            }
        }
    }

    return Math.max(currentMax, currentCount);
};

/**
 * Update {availabilities} object with new property 'numConsecutiveAvailable' that shows a count of max consecutive available dates
 * @param {object} campsites - original availabilities data for all campsites at a campground
 * @param {string|date} day - starting date
 * @returns {object} - new availabilities object with new property 'numConsecutiveAvailable'
 */
const updateDataWithNumConsecutiveAvailable = (
    campsites: LocalCampsite[],
    day: string
) => {
    let startRange = UTCStringToCalendarDate(day);
    let startClone = startRange;
    const endRange = startRange.add({ days: NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX });

    while (startClone.compare(endRange) < 0) {
        const dateKey = formatAvailabilityAccessor(startClone);
        const hasAnAvailableDay = some(campsites, (campsite) => {
            const status = campsite.availabilities[dateKey];
            return (
                status === CAMPSITE_AVAILABLE ||
                status === CAMPSITE_EARLY_ACCESS_AVAILABLE
            );
        });

        if (hasAnAvailableDay) {
            startRange = startClone;
            break;
        }

        startClone = startClone.add({ days: 1 });
    }

    // For each campsite's availabilities...
    const newAvailabilities = map(campsites, (availabilitiesObjByCampsite) => {
        const newAvailabilitiesObjByCampsite = { ...availabilitiesObjByCampsite };
        const { availabilities: availabilitiesByCampsite } = availabilitiesObjByCampsite;

        // count the max number of consecutive availabilities at the campsite
        const numConsecutiveAvailable = countMaxContinuousAvailability(
            startRange,
            endRange,
            availabilitiesByCampsite
        );

        newAvailabilitiesObjByCampsite.numConsecutiveAvailable = numConsecutiveAvailable;

        return newAvailabilitiesObjByCampsite;
    });
    return newAvailabilities;
};

/**
 * returns sorting order of 2 items based on their availability statuses
 * @param {object} a - comparison a
 * @param {object} b - comparison b
 * @param {date} dateKey - date to sort by
 * @param {*} isAsc - if sort order should be isAsc
 * @returns -1 or 1
 */
export const getSortOrderByAvailabilities = (
    a: LocalCampsite,
    b: LocalCampsite,
    dateKey: string,
    isAsc: boolean
) => {
    // 1) Get availability statuses for both comparison items on dateKey

    /* NOTE: instead of BE returning 'Not Available' or 'Closed' unavailable statuses,
    the status could also be NULL when the date key doesn't exists in the data at all,
    this could happen if a campsite is outside of its release period. In terms of sorting,
    we'll treat it the same as the unavailable statuses */
    const aAvailabilityStatus = a.availabilities?.[dateKey];
    const bAvailabilityStatus = b.availabilities?.[dateKey];

    // 2) Compare
    let result = 0;
    // if aAvailabilityStatus is NULL, return 1
    if (isNil(aAvailabilityStatus)) {
        result = 1;
    }
    // else if bAvailabilityStatus is NULL, return -1
    else if (isNil(bAvailabilityStatus)) {
        result = -1;
    }
    // else, both aAvailabilityStatus and bAvailabilityStatus are defined, so compare them based on their statuses' priority
    else {
        let aAvailabilityPriority =
            AVAILABILITY_STATUS_SORT_PRIORITY[aAvailabilityStatus];
        let bAvailabilityPriority =
            AVAILABILITY_STATUS_SORT_PRIORITY[bAvailabilityStatus];

        if (isValidUUID(aAvailabilityStatus)) {
            aAvailabilityPriority = AVAILABILITY_STATUS_SORT_PRIORITY[CAMPSITE_RESERVED];
        }
        if (isValidUUID(bAvailabilityStatus)) {
            bAvailabilityPriority = AVAILABILITY_STATUS_SORT_PRIORITY[CAMPSITE_RESERVED];
        }

        // if item b's status has higher priority
        if (aAvailabilityPriority < bAvailabilityPriority) {
            result = 1;
        }
        // if item a's status has higher priority
        else if (aAvailabilityPriority > bAvailabilityPriority) {
            result = -1;
        }
    }

    return isAsc ? result : -result;
};

/**
 * returns sorting order of 2 items based on their number of consecutive availabilities provided
 * @param {object} a - comparison a
 * @param {object} b - comparison b
 * @returns -1 or 1
 */
export const getSortOrderByConsecutiveAvailabilities = (
    a: LocalCampsite,
    b: LocalCampsite,
    isAsc: boolean
) => {
    if (a.numConsecutiveAvailable === b.numConsecutiveAvailable) {
        return getSortOrderOfStringWithNumbers(a, b, 'site');
    }

    return isAsc
        ? b.numConsecutiveAvailable - a.numConsecutiveAvailable
        : a.numConsecutiveAvailable - b.numConsecutiveAvailable;
};

/**
 * @param {array} dataSource - availability data
 * @param {string} day - day to be sorted by
 * @param {bool} isAsc
 * Provided [dataSource], sort by campsite availabilities on [day]
 */
export const getSortedDataByAvailability = ({
    dataSource: rawDataSource,
    day,
    isAsc = true,
    gridStartDate,
}: {
    dataSource: LocalCampsite[];
    day: string;
    isAsc: boolean;
    gridStartDate: string;
}) => {
    const todayString = calendarDateToUtcString(localToday);
    const isSortedByAvailabilityRange = day === 'AVAILABILITY';
    // eslint-disable-next-line no-param-reassign
    isAsc = isSortedByAvailabilityRange ? true : isAsc;

    /* if we need to sort by consecutive availabilities (e.g. on initial page load), calculate the max number of
    consecutive available dates from [day] to end of availability data for each campsite and modify the dataSource */
    let dataSource = cloneDeep(rawDataSource);
    let sortedData;
    if (isSortedByAvailabilityRange) {
        const sortDate = gridStartDate || (todayString as string);
        dataSource = updateDataWithNumConsecutiveAvailable(rawDataSource, sortDate);
        sortedData = dataSource.sort((a, b) => {
            return getSortOrderByConsecutiveAvailabilities(a, b, isAsc);
        });
    } else {
        const [month, dayNum, year] = day.split('-');
        const key = formatAvailabilityAccessor(
            new CalendarDate(
                parseInt(year, 10),
                parseInt(month, 10),
                parseInt(dayNum, 10)
            )
        );

        sortedData = dataSource.sort((a, b) => {
            // get sort order by availability statuses via date key
            const availabilitySortOrder = getSortOrderByAvailabilities(a, b, key, isAsc);

            // (2) get descending sort order by site name as secondary sort
            const nameSortOrder = getSortOrderOfStringWithNumbers(a, b, 'name');

            return availabilitySortOrder || nameSortOrder;
        });
    }

    return sortedData;
};

/**
 * @param {array} dataSource - availability data, each item carrying an `availabilityStatus` label
 * Provided [dataSource], sort by availability label (Available, Partially Available,
 * First-come First-served, Not Yet Released, Unavailable) with site name as secondary sort
 */
export const getSortedDataByAvailabilityLabel = ({
    dataSource,
}: {
    dataSource: LocalCampsite[];
}) => {
    const labelPriority: Record<string, number> = AVAILABILITY_LABEL_SORT_PRIORITY;
    const getPriority = (campsite: LocalCampsite) =>
        labelPriority[campsite.availabilityStatus as string] ??
        labelPriority[LABEL_UNAVAILABLE];

    return [...dataSource].sort((a, b) => {
        const labelSortOrder = getPriority(a) - getPriority(b);

        return labelSortOrder || getSortOrderOfStringWithNumbers(a, b, 'name');
    });
};

/**
 * @param {array} dataSource - availability data
 * @param {object} reservation - current reservation
 * Provided [dataSource], sort all reserved campsites to the top of the array.
 */
export const sortReservedToTop = (dataSource: Campsite[], reservation: any) => {
    // Sometimes if the dataSource has been reduced by the use of filters, there
    // could be undefined items in the dataSource array which causes lodash sortBy to fail
    const cleanedDataSource = dataSource.filter((item) => item !== undefined);

    const sortedSet = sortBy(cleanedDataSource, (item) => {
        const reservedCampsite = find(
            reservation.night_map,
            (campsite) => campsite.campsite_id === item.campsite_id
        );
        return reservedCampsite ? 0 : 1;
    });

    return sortedSet;
};

export const sortLineItems = (lineItems: LineItem[]) => {
    if (!lineItems || !lineItems.length) {
        return lineItems;
    }

    return lineItems.sort((a, b) => {
        const aFeeType = FEE_TYPES[a.fee.fee_type] || '';
        const bFeeType = FEE_TYPES[b.fee.fee_type] || '';

        const aIndex = FEE_TYPES_ORDER.indexOf(aFeeType);
        const bIndex = FEE_TYPES_ORDER.indexOf(bFeeType);

        // Handle unknown fee types (not found in order array)
        const aIsUnknown = aIndex === -1;
        const bIsUnknown = bIndex === -1;

        if (aIsUnknown && !bIsUnknown) return 1; // a goes to end
        if (!aIsUnknown && bIsUnknown) return -1; // b goes to end
        if (aIsUnknown && bIsUnknown) {
            // Both unknown, sort alphabetically
            return a.fee.description.localeCompare(b.fee.description);
        }

        // Primary sort by fee type order
        if (aIndex !== bIndex) {
            return aIndex - bIndex;
        }

        // Secondary sort alphabetically by description
        return a.fee.description.localeCompare(b.fee.description);
    });
};
